// Flat charts beside the 3D scene: a temperature cross-section and a
// single-point temperature profile, both in the visitor's units.
import { bedDepthAt, lakeIdAt, temperatureAt } from "./data.js";
import { distanceKm } from "./geo.js";
import { convertUnitValue, unitPreference, unitSymbol } from "../units.js";
import { paletteColor } from "./layers.js";
import { TEMPERATURE_STOPS } from "../palette-stops.js";

const LAND = [26, 36, 46];
const UNAVAILABLE = [39, 62, 78];
const TEXT = "rgba(220, 236, 244, 0.86)";
const GRID = "rgba(220, 236, 244, 0.14)";

function prepare(canvas, maxPixelRatio = 2) {
  const ratio = Math.min(window.devicePixelRatio || 1, maxPixelRatio);
  const width = Math.max(1, Math.round(canvas.clientWidth * ratio));
  const height = Math.max(1, Math.round(canvas.clientHeight * ratio));
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const context = canvas.getContext("2d");
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.clearRect(0, 0, width, height);
  return { context, width, height, ratio };
}

// "Nice" tick step for a span in display units.
function tickStep(span, count) {
  const raw = span / Math.max(1, count);
  const power = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / power;
  return (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10) * power;
}

function depthUnit() { return unitPreference("depth"); }
function temperatureUnit() { return unitPreference("waterTemperature"); }
function distanceUnit() { return unitPreference("distance"); }

export function drawSection(canvas, { volumes, field, lakeId, start, end, range, targetC, sliceDepth, dynamicScale = true }) {
  const { context, width, height, ratio } = prepare(canvas);
  const left = 44 * ratio;
  const right = 8 * ratio;
  const top = 8 * ratio;
  const bottom = 22 * ratio;
  const plotWidth = Math.max(1, Math.round(width - left - right));
  const plotHeight = Math.max(1, Math.round(height - top - bottom));
  const columns = [];
  let deepest = 0;
  for (let x = 0; x < plotWidth; x += 1) {
    const t = x / Math.max(1, plotWidth - 1);
    const latitude = start[0] + (end[0] - start[0]) * t;
    const longitude = start[1] + (end[1] - start[1]) * t;
    const id = lakeIdAt(field, latitude, longitude);
    const inLake = id > 0 && (!lakeId || id === lakeId);
    const bed = inLake ? bedDepthAt(field, latitude, longitude) : 0;
    columns.push([latitude, longitude, bed, volumes.get(id)]);
    deepest = Math.max(deepest, bed);
  }
  if (deepest <= 0) {
    context.fillStyle = TEXT;
    context.font = `${12 * ratio}px system-ui, sans-serif`;
    context.fillText("This line does not cross a lake in this view.", left, top + 16 * ratio);
    return null;
  }
  const maxDepth = deepest * 1.04;
  const image = context.createImageData(plotWidth, plotHeight);
  const values = new Float32Array(plotWidth * plotHeight).fill(NaN);
  let sectionLow = Infinity;
  let sectionHigh = -Infinity;
  for (let x = 0; x < plotWidth; x += 1) {
    const [latitude, longitude, bed, volume] = columns[x];
    for (let y = 0; y < plotHeight; y += 1) {
      const depth = (y + 0.5) / plotHeight * maxDepth;
      if (depth >= bed) continue;
      const value = volume ? temperatureAt(volume, latitude, longitude, depth) : NaN;
      values[y * plotWidth + x] = value;
      if (Number.isFinite(value)) {
        sectionLow = Math.min(sectionLow, value);
        sectionHigh = Math.max(sectionHigh, value);
      }
    }
  }
  let colorRange = dynamicScale && Number.isFinite(sectionLow) ? [sectionLow, sectionHigh] : [...range];
  if (colorRange[1] - colorRange[0] < 1e-6) colorRange = [colorRange[0] - 0.5, colorRange[1] + 0.5];
  const span = colorRange[1] - colorRange[0] || 1;
  for (let x = 0; x < plotWidth; x += 1) {
    const [, , bed] = columns[x];
    for (let y = 0; y < plotHeight; y += 1) {
      const depth = (y + 0.5) / plotHeight * maxDepth;
      const pixel = (y * plotWidth + x) * 4;
      if (depth >= bed) {
        image.data.set([...LAND, 255], pixel);
        continue;
      }
      const value = values[y * plotWidth + x];
      const [r, g, b] = Number.isFinite(value) ? paletteColor((value - colorRange[0]) / span, TEMPERATURE_STOPS) : UNAVAILABLE;
      image.data.set([r, g, b, 255], pixel);
    }
  }
  if (Number.isFinite(targetC)) {
    for (let y = 1; y < plotHeight; y += 1) {
      for (let x = 1; x < plotWidth; x += 1) {
        const value = values[y * plotWidth + x];
        const above = values[(y - 1) * plotWidth + x];
        const before = values[y * plotWidth + x - 1];
        const crosses = (other) => Number.isFinite(value) && Number.isFinite(other) && (value - targetC) * (other - targetC) <= 0 && value !== other;
        if (crosses(above) || crosses(before)) image.data.set([255, 255, 255, 255], (y * plotWidth + x) * 4);
      }
    }
  }
  context.putImageData(image, Math.round(left), Math.round(top));

  // Lakebed outline and the depth the slice is showing.
  context.save();
  context.beginPath();
  let drawing = false;
  columns.forEach(([, , bed], x) => {
    if (bed <= 0) { drawing = false; return; }
    const y = top + Math.min(1, bed / maxDepth) * plotHeight;
    if (drawing) context.lineTo(left + x + 0.5, y);
    else context.moveTo(left + x + 0.5, y);
    drawing = true;
  });
  context.strokeStyle = "rgba(236, 246, 250, 0.9)";
  context.lineWidth = 1.5 * ratio;
  context.lineJoin = "round";
  context.stroke();
  if (Number.isFinite(sliceDepth) && sliceDepth < maxDepth) {
    const y = top + (sliceDepth / maxDepth) * plotHeight;
    context.beginPath();
    context.moveTo(left, y);
    context.lineTo(left + plotWidth, y);
    context.setLineDash([5 * ratio, 4 * ratio]);
    context.strokeStyle = "rgba(255, 223, 146, 0.95)";
    context.lineWidth = 1.2 * ratio;
    context.stroke();
  }
  context.restore();
  context.font = `${10.5 * ratio}px system-ui, sans-serif`;
  context.fillStyle = TEXT;
  context.strokeStyle = GRID;
  context.lineWidth = ratio;
  const depthUnits = depthUnit();
  const maxDisplayDepth = convertUnitValue(maxDepth, "m", depthUnits);
  const depthStep = tickStep(maxDisplayDepth, 4);
  context.textAlign = "right";
  context.textBaseline = "middle";
  for (let tick = 0; tick <= maxDisplayDepth; tick += depthStep) {
    const y = top + (tick / maxDisplayDepth) * plotHeight;
    context.beginPath();
    context.moveTo(left, y);
    context.lineTo(left + plotWidth, y);
    context.stroke();
    context.fillText(`${Math.round(tick)}`, left - 5 * ratio, y);
  }
  context.save();
  context.translate(11 * ratio, top + plotHeight / 2);
  context.rotate(-Math.PI / 2);
  context.textAlign = "center";
  context.fillText(`Depth (${unitSymbol("depth")})`, 0, 0);
  context.restore();

  const length = distanceKm(start, end);
  const displayLength = convertUnitValue(length * 1000, "m", distanceUnit());
  const distanceStep = tickStep(displayLength, 5);
  context.textAlign = "center";
  context.textBaseline = "top";
  for (let tick = 0; tick <= displayLength + 1e-6; tick += distanceStep) {
    const x = left + (tick / displayLength) * plotWidth;
    context.fillText(`${Math.round(tick)}`, x, top + plotHeight + 5 * ratio);
  }
  context.textAlign = "right";
  context.fillText(unitSymbol("distance"), left + plotWidth, top + plotHeight + 5 * ratio);
  return { lengthKm: length, maxDepth: deepest, colorRange };
}

export function drawProfile(canvas, { points, range, sliceDepth, targetC }) {
  const { context, width, height, ratio } = prepare(canvas, 3);
  if (!points.length) return;
  const left = 30 * ratio;
  const right = 8 * ratio;
  const top = 14 * ratio;
  const bottom = 6 * ratio;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const maxDepth = Math.max(points[points.length - 1][0], 1);
  const units = temperatureUnit();
  const toDisplay = (value) => convertUnitValue(value, "C", units);
  let low = Math.min(range[0], ...points.map(([, value]) => value));
  let high = Math.max(range[1], ...points.map(([, value]) => value));
  if (high - low < 1) { low -= 0.5; high += 0.5; }
  const x = (value) => left + ((value - low) / (high - low)) * plotWidth;
  const y = (depth) => top + (depth / maxDepth) * plotHeight;

  const colorAt = (value, alpha) => {
    const [r, g, b] = paletteColor((value - range[0]) / (range[1] - range[0] || 1), TEMPERATURE_STOPS);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  context.strokeStyle = GRID;
  context.lineWidth = ratio;
  for (let tick = 0; tick <= 4; tick += 1) {
    const gridX = Math.round(left + (plotWidth * tick) / 4) + 0.5;
    context.beginPath();
    context.moveTo(gridX, top);
    context.lineTo(gridX, top + plotHeight);
    context.stroke();
  }

  // The area under the profile takes the temperature colour found at each depth.
  const area = context.createLinearGradient(0, top, 0, top + plotHeight);
  points.forEach(([depth, value]) => area.addColorStop(Math.min(1, depth / maxDepth), colorAt(value, 0.7)));
  context.fillStyle = area;
  context.beginPath();
  context.moveTo(left, y(points[0][0]));
  points.forEach(([depth, value]) => context.lineTo(x(value), y(depth)));
  context.lineTo(left, y(points[points.length - 1][0]));
  context.closePath();
  context.fill();

  context.font = `${10 * ratio}px system-ui, sans-serif`;
  context.fillStyle = TEXT;
  context.textBaseline = "bottom";
  context.textAlign = "left";
  context.fillText(`${Math.round(toDisplay(low))}${unitSymbol("waterTemperature")}`, left, top - 2 * ratio);
  context.textAlign = "right";
  context.fillText(`${Math.round(toDisplay(high))}${unitSymbol("waterTemperature")}`, left + plotWidth, top - 2 * ratio);
  context.textBaseline = "middle";
  context.fillText("0", left - 4 * ratio, top + 4 * ratio);
  context.fillText(`${Math.round(convertUnitValue(maxDepth, "m", depthUnit()))}`, left - 4 * ratio, top + plotHeight - 4 * ratio);

  context.lineWidth = ratio;
  if (Number.isFinite(targetC) && targetC >= low && targetC <= high) {
    context.strokeStyle = "rgba(255, 255, 255, 0.6)";
    context.setLineDash([3 * ratio, 3 * ratio]);
    context.beginPath();
    context.moveTo(x(targetC), top);
    context.lineTo(x(targetC), top + plotHeight);
    context.stroke();
    context.setLineDash([]);
  }
  if (Number.isFinite(sliceDepth) && sliceDepth <= maxDepth) {
    context.strokeStyle = "rgba(255, 223, 146, 0.85)";
    context.setLineDash([4 * ratio, 3 * ratio]);
    context.beginPath();
    context.moveTo(left, y(sliceDepth));
    context.lineTo(left + plotWidth, y(sliceDepth));
    context.stroke();
    context.setLineDash([]);
  }
  context.strokeStyle = "#ffffff";
  context.lineWidth = 2 * ratio;
  context.lineJoin = "round";
  context.beginPath();
  points.forEach(([depth, value], index) => {
    if (index) context.lineTo(x(value), y(depth));
    else context.moveTo(x(value), y(depth));
  });
  context.stroke();
}
